import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

interface SEOScoringRule {
  id: string;
  name: string;
  description: string;
  category: 'url_structure' | 'title_tag' | 'meta_description' | 'headings_structure' | 
           'content_quality' | 'linking_structure' | 'media_optimization' | 'user_experience' |
           'technical_seo' | 'engagement_signals' | 'performance_metrics';
  conditions: {
    field: string;
    operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'between' | 'exists' | 'not_exists' | 'contains' | 'not_contains';
    value: any;
    value2?: any; // For 'between' operator
  }[];
  scoring: {
    base_score: number;
    adjustments: {
      condition: string;
      score_modifier: number;
    }[];
  };
  priority: number;
  enabled: boolean;
}

interface ScoringCategory {
  key: string;
  title: string;
  icon: string;
  description: string;
  base_weight: number;
}

const SEOScoringAdmin: React.FC = () => {
  const [scoringRules, setScoringRules] = useState<SEOScoringRule[]>([]);
  const [categories] = useState<ScoringCategory[]>([
    { key: 'url_structure', title: 'URL & Structure', icon: '🔗', description: 'URL optimization and clean structure', base_weight: 8 },
    { key: 'title_tag', title: 'Title Tag', icon: '📝', description: 'Page title optimization', base_weight: 15 },
    { key: 'meta_description', title: 'Meta Description', icon: '📄', description: 'Meta description effectiveness', base_weight: 10 },
    { key: 'headings_structure', title: 'Headings (H1-H6)', icon: '🏗️', description: 'Heading hierarchy and structure', base_weight: 12 },
    { key: 'content_quality', title: 'Content Quality', icon: '✍️', description: 'Content depth and relevance', base_weight: 20 },
    { key: 'linking_structure', title: 'Internal & External Linking', icon: '🔗', description: 'Link quality and distribution', base_weight: 8 },
    { key: 'media_optimization', title: 'Media & Visual Elements', icon: '🖼️', description: 'Image optimization and alt text', base_weight: 7 },
    { key: 'user_experience', title: 'User Experience (UX)', icon: '👥', description: 'Mobile-friendly and page speed', base_weight: 10 },
    { key: 'technical_seo', title: 'Technical SEO', icon: '⚙️', description: 'Technical foundation and schema', base_weight: 8 },
    { key: 'engagement_signals', title: 'Engagement & Conversion', icon: '🎯', description: 'CTA and conversion elements', base_weight: 1 },
    { key: 'performance_metrics', title: 'Performance Monitoring', icon: '📊', description: 'Analytics and tracking setup', base_weight: 1 }
  ]);
  const [selectedCategory, setSelectedCategory] = useState<string>('title_tag');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<SEOScoringRule | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScoringRules();
  }, []);

  const loadScoringRules = async () => {
    try {
      const response = await apiFetch('/api/admin/seo-scoring-rules', {
        headers: {
          'X-Tenant-ID': '1'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setScoringRules(data.rules || []);
      } else {
        // Load default rules if none exist
        setDefaultRules();
      }
    } catch (error) {
      console.error('Error loading scoring rules:', error);
      setDefaultRules();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultRules = () => {
    const defaultRules: SEOScoringRule[] = [
      {
        id: 'title_length_optimal',
        name: 'Title Length Optimal',
        description: 'Title tag length is in optimal range (50-60 characters)',
        category: 'title_tag',
        conditions: [
          { field: 'title_tag_length', operator: 'between', value: 50, value2: 60 }
        ],
        scoring: {
          base_score: 95,
          adjustments: []
        },
        priority: 1,
        enabled: true
      },
      {
        id: 'title_length_too_short',
        name: 'Title Too Short',
        description: 'Title tag is too short (less than 30 characters)',
        category: 'title_tag',
        conditions: [
          { field: 'title_tag_length', operator: 'less_than', value: 30 }
        ],
        scoring: {
          base_score: 40,
          adjustments: []
        },
        priority: 2,
        enabled: true
      },
      {
        id: 'title_length_too_long',
        name: 'Title Too Long',
        description: 'Title tag is too long (more than 70 characters)',
        category: 'title_tag',
        conditions: [
          { field: 'title_tag_length', operator: 'greater_than', value: 70 }
        ],
        scoring: {
          base_score: 60,
          adjustments: []
        },
        priority: 3,
        enabled: true
      },
      {
        id: 'meta_description_optimal',
        name: 'Meta Description Optimal',
        description: 'Meta description length is in optimal range (120-160 characters)',
        category: 'meta_description',
        conditions: [
          { field: 'meta_description_length', operator: 'between', value: 120, value2: 160 }
        ],
        scoring: {
          base_score: 90,
          adjustments: []
        },
        priority: 1,
        enabled: true
      },
      {
        id: 'meta_description_missing',
        name: 'Meta Description Missing',
        description: 'Meta description is not set',
        category: 'meta_description',
        conditions: [
          { field: 'meta_description', operator: 'not_exists', value: null }
        ],
        scoring: {
          base_score: 30,
          adjustments: []
        },
        priority: 2,
        enabled: true
      },
      {
        id: 'h1_single',
        name: 'Single H1 Tag',
        description: 'Page has exactly one H1 tag',
        category: 'headings_structure',
        conditions: [
          { field: 'h1_count', operator: 'equals', value: 1 }
        ],
        scoring: {
          base_score: 85,
          adjustments: [
            { condition: 'h2_count > 0', score_modifier: 10 }
          ]
        },
        priority: 1,
        enabled: true
      },
      {
        id: 'h1_missing',
        name: 'H1 Tag Missing',
        description: 'Page has no H1 tag',
        category: 'headings_structure',
        conditions: [
          { field: 'h1_count', operator: 'equals', value: 0 }
        ],
        scoring: {
          base_score: 40,
          adjustments: []
        },
        priority: 2,
        enabled: true
      },
      {
        id: 'h1_multiple',
        name: 'Multiple H1 Tags',
        description: 'Page has more than one H1 tag',
        category: 'headings_structure',
        conditions: [
          { field: 'h1_count', operator: 'greater_than', value: 1 }
        ],
        scoring: {
          base_score: 60,
          adjustments: []
        },
        priority: 3,
        enabled: true
      },
      {
        id: 'content_comprehensive',
        name: 'Comprehensive Content',
        description: 'Content is comprehensive (500+ words)',
        category: 'content_quality',
        conditions: [
          { field: 'word_count', operator: 'greater_than', value: 500 }
        ],
        scoring: {
          base_score: 85,
          adjustments: [
            { condition: 'word_count > 1000', score_modifier: 10 }
          ]
        },
        priority: 1,
        enabled: true
      },
      {
        id: 'content_minimal',
        name: 'Minimal Content',
        description: 'Content is too short (less than 300 words)',
        category: 'content_quality',
        conditions: [
          { field: 'word_count', operator: 'less_than', value: 300 }
        ],
        scoring: {
          base_score: 40,
          adjustments: []
        },
        priority: 2,
        enabled: true
      },
      {
        id: 'images_optimized',
        name: 'Images Fully Optimized',
        description: 'All images have alt text',
        category: 'media_optimization',
        conditions: [
          { field: 'images_with_alt_percentage', operator: 'equals', value: 100 }
        ],
        scoring: {
          base_score: 90,
          adjustments: []
        },
        priority: 1,
        enabled: true
      },
      {
        id: 'images_missing_alt',
        name: 'Images Missing Alt Text',
        description: 'Some or all images are missing alt text',
        category: 'media_optimization',
        conditions: [
          { field: 'images_with_alt_percentage', operator: 'less_than', value: 100 }
        ],
        scoring: {
          base_score: 50,
          adjustments: [
            { condition: 'images_with_alt_percentage > 50', score_modifier: 20 }
          ]
        },
        priority: 2,
        enabled: true
      }
    ];
    
    setScoringRules(defaultRules);
  };

  const saveScoringRules = async () => {
    try {
      const response = await apiFetch('/api/admin/seo-scoring-rules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': '1'
        },
        body: JSON.stringify({ rules: scoringRules })
      });

      if (response.ok) {
        toast.success('SEO scoring rules saved successfully');
      } else {
        toast.error('Failed to save scoring rules');
      }
    } catch (error) {
      console.error('Error saving scoring rules:', error);
      toast.error('Failed to save scoring rules');
    }
  };

  const getRulesForCategory = (category: string) => {
    return scoringRules.filter(rule => rule.category === category && rule.enabled);
  };

  const openRuleModal = (rule?: SEOScoringRule) => {
    setEditingRule(rule || null);
    setShowRuleModal(true);
  };

  const closeRuleModal = () => {
    setShowRuleModal(false);
    setEditingRule(null);
  };

  const saveRule = (rule: SEOScoringRule) => {
    if (editingRule) {
      // Update existing rule
      setScoringRules(prev => prev.map(r => r.id === rule.id ? rule : r));
    } else {
      // Add new rule
      setScoringRules(prev => [...prev, { ...rule, id: `rule_${Date.now()}` }]);
    }
    closeRuleModal();
  };

  const deleteRule = (ruleId: string) => {
    setScoringRules(prev => prev.filter(r => r.id !== ruleId));
  };

  const toggleRuleEnabled = (ruleId: string) => {
    setScoringRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading SEO scoring configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">SEO Scoring Administration</h1>
              <p className="text-slate-400">
                Configure detailed scoring rules for individual SEO metrics and conditions
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => openRuleModal()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Add New Rule
              </button>
              <button
                onClick={saveScoringRules}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                Save All Changes
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Category Selector */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 sticky top-4">
              <h2 className="text-lg font-semibold text-white mb-4">SEO Categories</h2>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.key}
                    onClick={() => setSelectedCategory(category.key)}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      selectedCategory === category.key
                        ? 'bg-blue-600/20 border-blue-500/50 text-blue-400'
                        : 'bg-slate-700/30 hover:bg-slate-700/50 text-slate-300'
                    } border`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{category.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{category.title}</p>
                        <p className="text-xs opacity-75 truncate">{getRulesForCategory(category.key).length} rules</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Rules Management */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
              {/* Selected Category Header */}
              {(() => {
                const category = categories.find(c => c.key === selectedCategory);
                return category ? (
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <span className="text-3xl">{category.icon}</span>
                      <div>
                        <h2 className="text-2xl font-bold text-white">{category.title}</h2>
                        <p className="text-slate-400">{category.description}</p>
                        <p className="text-sm text-slate-500">Base weight: {category.base_weight}% of overall score</p>
                      </div>
                    </div>
                    <button
                      onClick={() => openRuleModal()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Add Rule
                    </button>
                  </div>
                ) : null;
              })()}

              {/* Rules List */}
              <div className="space-y-4">
                {getRulesForCategory(selectedCategory).length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-400 mb-4">No scoring rules configured for this category</p>
                    <button
                      onClick={() => openRuleModal()}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                    >
                      Create First Rule
                    </button>
                  </div>
                ) : (
                  getRulesForCategory(selectedCategory)
                    .sort((a, b) => a.priority - b.priority)
                    .map((rule) => (
                      <div
                        key={rule.id}
                        className={`border rounded-xl p-4 transition-all ${
                          rule.enabled
                            ? 'border-slate-600/50 bg-slate-700/20'
                            : 'border-slate-700/30 bg-slate-800/30 opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-semibold text-white">{rule.name}</h3>
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                Score: {rule.scoring.base_score}
                              </span>
                              <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                                Priority: {rule.priority}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mb-3">{rule.description}</p>
                            
                            {/* Conditions Display */}
                            <div className="mb-3">
                              <p className="text-xs text-slate-500 mb-1">Conditions:</p>
                              <div className="space-y-1">
                                {rule.conditions.map((condition, index) => (
                                  <div key={index} className="text-xs bg-slate-800/50 px-2 py-1 rounded">
                                    <code className="text-cyan-400">
                                      {condition.field} {condition.operator} {condition.value}
                                      {condition.value2 !== undefined && ` and ${condition.value2}`}
                                    </code>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Score Adjustments */}
                            {rule.scoring.adjustments.length > 0 && (
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Score Adjustments:</p>
                                <div className="space-y-1">
                                  {rule.scoring.adjustments.map((adjustment, index) => (
                                    <div key={index} className="text-xs bg-slate-800/50 px-2 py-1 rounded">
                                      <code className="text-yellow-400">
                                        If {adjustment.condition} then {adjustment.score_modifier > 0 ? '+' : ''}{adjustment.score_modifier}
                                      </code>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => toggleRuleEnabled(rule.id)}
                              className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                rule.enabled
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  : 'bg-slate-500/20 text-slate-400 hover:bg-slate-500/30'
                              }`}
                            >
                              {rule.enabled ? 'Enabled' : 'Disabled'}
                            </button>
                            <button
                              onClick={() => openRuleModal(rule)}
                              className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => deleteRule(rule.id)}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Rule Modal */}
        {showRuleModal && (
          <RuleModal
            rule={editingRule}
            category={selectedCategory}
            onSave={saveRule}
            onClose={closeRuleModal}
          />
        )}
    </div>
  );
};

// Rule Modal Component
interface RuleModalProps {
  rule: SEOScoringRule | null;
  category: string;
  onSave: (rule: SEOScoringRule) => void;
  onClose: () => void;
}

const RuleModal: React.FC<RuleModalProps> = ({ rule, category, onSave, onClose }) => {
  const [formData, setFormData] = useState<SEOScoringRule>({
    id: rule?.id || '',
    name: rule?.name || '',
    description: rule?.description || '',
    category: category as any,
    conditions: rule?.conditions || [{ field: '', operator: 'equals', value: '' }],
    scoring: rule?.scoring || { base_score: 70, adjustments: [] },
    priority: rule?.priority || 1,
    enabled: rule?.enabled ?? true
  });

  const fieldOptions = {
    url_structure: ['url_length', 'has_https', 'has_underscores', 'url_depth'],
    title_tag: ['title_tag', 'title_tag_length', 'title_has_keyword'],
    meta_description: ['meta_description', 'meta_description_length'],
    headings_structure: ['h1_count', 'h2_count', 'h3_count', 'heading_hierarchy'],
    content_quality: ['word_count', 'reading_level', 'keyword_density'],
    linking_structure: ['internal_links', 'external_links', 'broken_links'],
    media_optimization: ['image_count', 'images_with_alt', 'images_with_alt_percentage'],
    user_experience: ['mobile_friendly', 'page_load_time', 'core_web_vitals'],
    technical_seo: ['https_enabled', 'canonical_tag', 'schema_markup'],
    engagement_signals: ['has_cta', 'social_sharing', 'contact_info'],
    performance_metrics: ['analytics_setup', 'search_console_setup', 'conversion_tracking']
  };

  const operatorOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'between', label: 'Between' },
    { value: 'exists', label: 'Exists' },
    { value: 'not_exists', label: 'Does Not Exist' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: 'equals', value: '' }]
    }));
  };

  const updateCondition = (index: number, updates: Partial<SEOScoringRule['conditions'][0]>) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) => 
        i === index ? { ...condition, ...updates } : condition
      )
    }));
  };

  const removeCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const addScoreAdjustment = () => {
    setFormData(prev => ({
      ...prev,
      scoring: {
        ...prev.scoring,
        adjustments: [...prev.scoring.adjustments, { condition: '', score_modifier: 0 }]
      }
    }));
  };

  const updateScoreAdjustment = (index: number, updates: Partial<SEOScoringRule['scoring']['adjustments'][0]>) => {
    setFormData(prev => ({
      ...prev,
      scoring: {
        ...prev.scoring,
        adjustments: prev.scoring.adjustments.map((adjustment, i) => 
          i === index ? { ...adjustment, ...updates } : adjustment
        )
      }
    }));
  };

  const removeScoreAdjustment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      scoring: {
        ...prev.scoring,
        adjustments: prev.scoring.adjustments.filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {rule ? 'Edit Scoring Rule' : 'Create New Scoring Rule'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Rule Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Base Score
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.scoring.base_score}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    scoring: { ...prev.scoring, base_score: parseInt(e.target.value) }
                  }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Priority
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Conditions</h3>
              <button
                type="button"
                onClick={addCondition}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
              >
                Add Condition
              </button>
            </div>
            <div className="space-y-3">
              {formData.conditions.map((condition, index) => (
                <div key={index} className="bg-slate-700/50 p-4 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <select
                      value={condition.field}
                      onChange={(e) => updateCondition(index, { field: e.target.value })}
                      className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                    >
                      <option value="">Select Field</option>
                      {(fieldOptions[category as keyof typeof fieldOptions] || []).map(field => (
                        <option key={field} value={field}>{field}</option>
                      ))}
                    </select>
                    <select
                      value={condition.operator}
                      onChange={(e) => updateCondition(index, { operator: e.target.value as any })}
                      className="px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                    >
                      {operatorOptions.map(op => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeCondition(index)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      placeholder="Value"
                      value={condition.value}
                      onChange={(e) => updateCondition(index, { value: e.target.value })}
                      className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                    />
                    {condition.operator === 'between' && (
                      <input
                        type="text"
                        placeholder="Value 2"
                        value={condition.value2 || ''}
                        onChange={(e) => updateCondition(index, { value2: e.target.value })}
                        className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Score Adjustments */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Score Adjustments</h3>
              <button
                type="button"
                onClick={addScoreAdjustment}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
              >
                Add Adjustment
              </button>
            </div>
            <div className="space-y-3">
              {formData.scoring.adjustments.map((adjustment, index) => (
                <div key={index} className="bg-slate-700/50 p-4 rounded-lg flex items-center space-x-3">
                  <input
                    type="text"
                    placeholder="Condition (e.g., word_count > 1000)"
                    value={adjustment.condition}
                    onChange={(e) => updateScoreAdjustment(index, { condition: e.target.value })}
                    className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                  />
                  <input
                    type="number"
                    placeholder="Score Modifier"
                    value={adjustment.score_modifier}
                    onChange={(e) => updateScoreAdjustment(index, { score_modifier: parseInt(e.target.value) })}
                    className="w-24 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeScoreAdjustment(index)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-700">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                className="rounded border-slate-500 bg-slate-700"
              />
              <span className="text-slate-200">Enabled</span>
            </label>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Save Rule
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SEOScoringAdmin;